import type { JSONContent } from '@tiptap/core'
import type { TFunction } from 'i18next'

export type DocTemplateId = 'spec' | 'meetingNotes' | 'plan'

export const DOC_TEMPLATE_IDS: ReadonlyArray<DocTemplateId> = ['spec', 'meetingNotes', 'plan']

type Block = { heading: string; body: 'text' | 'bullets' | 'todos' }

/** Section headings (i18n keys under docs.templates.<id>.sections) and what goes under each. */
const TEMPLATE_SECTIONS: Record<DocTemplateId, ReadonlyArray<Block>> = {
  spec: [
    { heading: 'problem', body: 'text' },
    { heading: 'goals', body: 'bullets' },
    { heading: 'nonGoals', body: 'bullets' },
    { heading: 'approach', body: 'text' },
    { heading: 'openQuestions', body: 'bullets' },
  ],
  meetingNotes: [
    { heading: 'attendees', body: 'bullets' },
    { heading: 'agenda', body: 'bullets' },
    { heading: 'notes', body: 'text' },
    { heading: 'actionItems', body: 'todos' },
  ],
  plan: [
    { heading: 'goal', body: 'text' },
    { heading: 'steps', body: 'todos' },
    { heading: 'risks', body: 'bullets' },
    { heading: 'done', body: 'bullets' },
  ],
}

const emptyParagraph: JSONContent = { type: 'paragraph' }

function bodyFor(kind: Block['body']): JSONContent {
  if (kind === 'text') return emptyParagraph
  if (kind === 'bullets') return { type: 'bulletList', content: [{ type: 'listItem', content: [emptyParagraph] }] }
  return { type: 'taskList', content: [{ type: 'taskItem', attrs: { checked: false }, content: [emptyParagraph] }] }
}

/** A starter outline for an empty doc (design 32a): headings with an empty block under each. */
export function buildDocTemplate(id: DocTemplateId, t: TFunction<'projects'>): Array<JSONContent> {
  return TEMPLATE_SECTIONS[id].flatMap((block) => [
    {
      type: 'heading',
      attrs: { level: 2 },
      content: [{ type: 'text', text: t(`docs.templates.${id}.sections.${block.heading}`) }],
    },
    bodyFor(block.body),
  ])
}
